/**
 * 会社の設定の1つのまとまりを変える画面（issue #124・決定22）。
 *
 * 決めごと:
 *  - **保存は「保存する」を押したときだけ**。押すと、変えるところを確認の窓でお見せしてから保存します
 *    （原則1・4）。「やめる」で会社の設定の一覧へ戻ります（入れたものは保存しません）
 *  - 「既定値に戻す」は、その欄を既定値にするだけで、保存するまでは何も変わりません
 *  - 数の範囲は確認の窓の前に欄の下でお伝えします。**保存してよいかはサーバが決めます**。
 *    合わない値は保存されず、サーバの理由（欄の名前入り）を
 *    そのまま出します。1つでも合わなければ、まとまりの中のどれも変わりません
 *  - **「まだ効きません」の設定**は欄にもそう出し、確認の窓でも「保存しても、いまの動きは変わりません」と
 *    お伝えします（効かない設定を黙って保存させない）
 *  - 保存日数を短くするときは、**今夜の処理で消える件数**をサーバに数えてもらい、確認の窓に出します
 *    （消えたものは元に戻せないため・issue #410 と同じ考え方）
 */
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import type { SettingItem, SettingRemoval, SettingsSaved, SettingsScreen } from '../api/types.js';
import { channelTypeLabel, postPolicyLabel, readReceiptLabel } from '../channels/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';
import {
  attachmentTypeLabel,
  canChoosePostPolicy,
  fromDraft,
  numericDraftErrors,
  presetText,
  sameSettingValue,
  settingValueText,
  SITE_TEMPLATE_MAX,
  SITE_TEMPLATE_NAME_MAX,
  SITE_TEMPLATE_TYPES,
  siteTemplateRowText,
  sortedTypes,
  switchWord,
  toDraft,
  type AllowedTypes,
  type PresetTable,
  type SettingDraft,
  type SiteTemplateRow,
} from '../settings/values.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
  reveal?: boolean;
}

/** 保存するもの1件（確認の窓に出す言葉つき）。 */
interface PendingChange {
  setting: SettingItem;
  reset: boolean;
  /** 送る値（既定値に戻すときは無し） */
  value?: unknown;
  after: unknown;
}

interface Confirming {
  changes: PendingChange[];
  removals: SettingRemoval[];
}

function n(value: number): string {
  return value.toLocaleString('ja-JP');
}

export function SettingsGroupPage(): ReactNode {
  const { group: groupKey } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<SettingsScreen | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, SettingDraft>>({});
  const [resets, setResets] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [busy, setBusy] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);
  const pageRef = useRef<HTMLElement>(null);
  const focusError = useRef(false);

  useEffect(() => {
    if (!focusError.current) return;
    focusError.current = false;
    const field = pageRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    field?.focus();
    field?.scrollIntoView({ block: 'center' });
  });

  const group = screen?.groups.find((item) => item.key === groupKey) ?? null;
  const types = screen ? channelTypesOf(screen) : [];

  /** サーバの値から、入力欄を作り直す（開いたとき・保存したあと）。 */
  const startEditing = useCallback((loaded: SettingsScreen) => {
    const knownTypes = channelTypesOf(loaded);
    const next: Record<string, SettingDraft> = {};
    for (const item of loaded.groups.flatMap((g) => g.settings)) {
      next[item.key] = toDraft(item, item.value, knownTypes);
    }
    setValidationAttempted(false);
    setScreen(loaded);
    setDrafts(next);
    setResets(new Set());
  }, []);

  useEffect(() => {
    let cancelled = false;
    setScreen(null);
    setError(null);
    api
      .get<SettingsScreen>('/admin/settings')
      .then((loaded) => {
        if (!cancelled) startEditing(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey, startEditing]);

  function editDraft(key: string, draft: SettingDraft): void {
    setDrafts((current) => ({ ...current, [key]: draft }));
    // 手で直したら、「既定値に戻す」は取り消す（直した値を保存する）
    setResets((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }

  function resetToDefault(setting: SettingItem): void {
    setDrafts((current) => ({
      ...current,
      [setting.key]: toDraft(setting, setting.default_value, types),
    }));
    setResets((current) => new Set(current).add(setting.key));
  }

  /** いまの入力と、保存されている値との違い。 */
  function collectChanges(): PendingChange[] {
    if (!group) return [];
    const changes: PendingChange[] = [];
    for (const setting of group.settings) {
      const draft = drafts[setting.key];
      if (!draft) continue;
      if (resets.has(setting.key)) {
        if (!setting.is_default) {
          changes.push({ setting, reset: true, after: setting.default_value });
        }
        continue;
      }
      const value = fromDraft(setting, draft);
      if (!sameSettingValue(setting, value, setting.value)) {
        changes.push({ setting, reset: false, value, after: value });
      }
    }
    return changes;
  }

  const changes = collectChanges();
  const draftErrors = Object.fromEntries(
    changes
      .filter(({ setting }) => !(setting.retention && !screen?.can_change_retention))
      .map(({ setting }) => [setting.key, numericDraftErrors(setting, drafts[setting.key])]),
  );

  function payload(changes: PendingChange[]): unknown[] {
    return changes.map((change) =>
      change.reset
        ? { key: change.setting.key, reset: true }
        : { key: change.setting.key, value: change.value },
    );
  }

  /** 「保存する」を押した（まだ保存しない。確認の窓を出す）。 */
  async function askSave(): Promise<void> {
    setResult(null);
    setValidationAttempted(true);
    if (Object.values(draftErrors).some((errors) => Object.keys(errors).length > 0)) {
      setResult({
        tone: 'error',
        message: '入力に誤りがあります。赤字の欄をご確認ください。',
        reveal: false,
      });
      focusError.current = true;
      return;
    }
    if (changes.length === 0) {
      setResult({ tone: 'info', message: '変えたところはありません。' });
      return;
    }
    let removals: SettingRemoval[] = [];
    if (changes.some((change) => change.setting.retention)) {
      // 保存日数を短くすると今夜消えるので、先に件数を数えてもらう（何も変わらない）
      setBusy(true);
      try {
        const counted = await api.post<{ removals: SettingRemoval[] }>('/admin/settings/preview', {
          body: { changes: payload(changes) },
        });
        removals = counted.removals;
      } catch (caught: unknown) {
        setResult({
          tone: 'error',
          message: withReason('保存できませんでした', toMessage(caught)),
          nextStep: '入力の内容をご確認のうえ、もう一度「保存する」を押してください。',
        });
        return;
      } finally {
        setBusy(false);
      }
    }
    setConfirming({ changes, removals });
  }

  async function save(): Promise<void> {
    if (!confirming) return;
    setBusy(true);
    try {
      const saved = await api.patch<SettingsSaved>('/admin/settings', {
        body: { changes: payload(confirming.changes) },
      });
      const notYet = confirming.changes.filter((change) => !change.setting.in_effect);
      setConfirming(null);
      startEditing(saved);
      setResult({
        tone: 'success',
        message:
          saved.changed.length > 0
            ? `保存しました（${saved.changed.length}件）。`
            : '保存しました（変わったところはありませんでした）。',
        ...(notYet.length > 0
          ? {
              nextStep: `${notYet.map((change) => `「${change.setting.label}」`).join('')}は、まだ効きません。いまの動きは変わりません。`,
            }
          : {
              nextStep:
                '次の操作から効きます（アプリの表示は、アプリを開き直したときに変わります）。',
            }),
      });
    } catch (caught: unknown) {
      setConfirming(null);
      setResult({
        tone: 'error',
        message: withReason('保存できませんでした', toMessage(caught)),
        nextStep: '入力の内容をご確認のうえ、もう一度「保存する」を押してください。',
      });
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <article>
        <PageHeader title="会社の設定" />
        <ErrorBlock failure={error} onRetry={() => setReloadKey((k) => k + 1)} />
      </article>
    );
  }
  if (!screen) return <LoadingBlock label="会社の設定を読み込んでいます…" />;
  if (!group) {
    return (
      <article>
        <PageHeader title="会社の設定" />
        <ErrorBlock
          message="この設定のまとまりは見つかりませんでした。"
          nextStep="会社の設定の一覧に戻って、開き直してください。"
        />
        <Link to="/settings">会社の設定の一覧へ戻る</Link>
      </article>
    );
  }

  const losesSomething = (confirming?.removals ?? []).some(
    (removal) => removal.message_count + removal.attachment_count + removal.record_count > 0,
  );

  return (
    <article ref={pageRef}>
      <PageHeader
        title={group.title}
        description={group.description}
        actions={
          <Link className="ui-button ui-button-secondary" to="/settings">
            会社の設定の一覧へ戻る
          </Link>
        }
      />

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          reveal={result.reveal ?? true}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}
      <FormCard
        noValidate
        submitLabel="保存する"
        submitBusyLabel="確かめています…"
        busy={busy}
        onSubmit={() => void askSave()}
        onCancel={() => navigate('/settings')}
      >
        {group.settings.map((setting) => (
          <SettingField
            key={setting.key}
            setting={setting}
            draft={drafts[setting.key]}
            errors={validationAttempted ? (draftErrors[setting.key] ?? {}) : {}}
            resetting={resets.has(setting.key)}
            locked={setting.retention && !screen.can_change_retention}
            onChange={(draft) => editDraft(setting.key, draft)}
            onReset={() => resetToDefault(setting)}
          />
        ))}
      </FormCard>

      <ConfirmDialog
        open={confirming !== null}
        // 今夜消えるものがあるときは、トークの詳細の同じ操作（「保存期間を短くする」）と同じく
        // 題とボタンで何が起きるかを言い、赤にする（docs/06 4-7・#448 L-11）
        title={losesSomething ? '保存期間を短くします' : '設定を保存します'}
        tone={losesSomething ? 'danger' : 'primary'}
        message={
          confirming ? (
            <>
              次のように変えます。
              {confirming.changes.map((change) => (
                <Fragment key={change.setting.key}>
                  {describeChange(change).map((line, index) => (
                    <Fragment key={index}>
                      <br />
                      {line}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
              {confirming.removals.map((removal, index) => (
                <Fragment key={`removal-${index}`}>
                  <br />
                  <strong>{removalSentence(removal)}</strong>
                </Fragment>
              ))}
              {confirming.changes
                .filter((change) => !change.setting.in_effect)
                .map((change) => (
                  <Fragment key={`not-yet-${change.setting.key}`}>
                    <br />「{change.setting.label}
                    」は、まだ効きません。保存しても、いまの動きは変わりません。
                  </Fragment>
                ))}
            </>
          ) : (
            ''
          )
        }
        reversibility={
          losesSomething
            ? '消えたものは元に戻せません。設定は、この画面でいつでも変え直せます。変えたことは操作履歴に残ります。'
            : '設定は、この画面でいつでも変え直せます。変えたことは操作履歴に残ります。'
        }
        reversible={!losesSomething}
        confirmLabel={losesSomething ? '保存期間を短くする' : '保存する'}
        busy={busy}
        onConfirm={() => void save()}
        onCancel={() => setConfirming(null)}
      />
    </article>
  );
}

/** 表で入れる設定（既定値の文が長くなるので、欄の下には出さず「既定値に戻す」だけを置く）。 */
const TABLE_EDITORS: ReadonlySet<string> = new Set([
  'channel_types',
  'retention_by_type',
  'attachment_types',
  'site_channel_template',
]);

/** トークの種類の一覧（種類ごとの決まりの表から）。 */
function channelTypesOf(screen: SettingsScreen): string[] {
  const presets = screen.groups
    .flatMap((group) => group.settings)
    .find((setting) => setting.key === 'channel.type_defaults');
  return sortedTypes(Object.keys((presets?.value ?? {}) as PresetTable));
}

/** 確認の窓の「◯◯：前 → 後」。一覧の形の値は、変わった種類・形式だけを書く。 */
function describeChange(change: PendingChange): string[] {
  const { setting, after } = change;
  const suffix = change.reset ? '（既定値に戻す）' : '';
  if (setting.editor === 'channel_types') {
    const before = (setting.value ?? {}) as PresetTable;
    const next = (after ?? {}) as PresetTable;
    const lines = sortedTypes([...Object.keys(before), ...Object.keys(next)])
      .filter((type) => JSON.stringify(before[type]) !== JSON.stringify(next[type]))
      .map((type) => `・${presetText(type, next[type])}${suffix}`);
    return [`「${setting.label}」`, ...lines];
  }
  if (setting.editor === 'retention_by_type') {
    const before = (setting.value ?? {}) as Record<string, number | null>;
    const next = (after ?? {}) as Record<string, number | null>;
    const days = (value: number | null | undefined): string =>
      value === null || value === undefined ? '無期限' : `${n(value)}日`;
    const lines = sortedTypes([...Object.keys(before), ...Object.keys(next)])
      .filter((type) => (before[type] ?? null) !== (next[type] ?? null))
      .map(
        (type) =>
          `・${channelTypeLabel(type)}：${days(before[type])} → ${days(next[type])}${suffix}`,
      );
    return [`「${setting.label}」`, ...lines];
  }
  if (setting.editor === 'site_channel_template') {
    const next = Array.isArray(after) ? (after as SiteTemplateRow[]) : [];
    return [
      `「${setting.label}」${suffix}`,
      ...(next.length === 0
        ? ['・拠点を足しても、トークは作りません']
        : next.map((row) => `・${siteTemplateRowText(row)}`)),
    ];
  }
  if (setting.editor === 'attachment_types') {
    const before = Object.keys((setting.value ?? {}) as AllowedTypes);
    const next = Object.keys((after ?? {}) as AllowedTypes);
    const all = (setting.default_value ?? {}) as AllowedTypes;
    const label = (type: string): string => attachmentTypeLabel(type, all[type] ?? []);
    const added = next.filter((type) => !before.includes(type)).map(label);
    const removed = before.filter((type) => !next.includes(type)).map(label);
    return [
      `「${setting.label}」${suffix}`,
      ...(added.length > 0 ? [`・送れるようにする：${added.join('、')}`] : []),
      ...(removed.length > 0 ? [`・送れないようにする：${removed.join('、')}`] : []),
    ];
  }
  return [
    `「${setting.label}」：${settingValueText(setting, setting.value)} → ${settingValueText(setting, after)}${suffix}`,
  ];
}

/** 入れられる数の範囲（「0回以上」「1〜10件」）。 */
function rangeText(setting: SettingItem): string {
  const unit = setting.unit ?? '';
  const min = setting.min ?? 0;
  if (setting.max === null) return `（${n(min)}${unit}以上）`;
  return `（${n(min)}〜${n(setting.max)}${unit}）`;
}

/**
 * 今夜消えるものの1文（件数つき）。件数はサーバが「今夜の処理までに日数を過ぎるもの」で数えた上限なので、
 * 「最大で」とお伝えする（元に戻せない削除を少なく見せない・issue #410）。
 */
function removalSentence(removal: SettingRemoval): string {
  if (removal.channel_type !== null) {
    const what = `「${channelTypeLabel(removal.channel_type)}」の保存日数を${removal.from}から${removal.to}にすると、`;
    if (removal.message_count === 0) {
      return `${what}今夜の処理で消えるメッセージはありません（これからは、${removal.to}を過ぎたものから消えます）。`;
    }
    const photos =
      removal.attachment_count > 0
        ? `、写真などのファイルが最大${n(removal.attachment_count)}件`
        : '';
    return `${what}今夜の処理で、メッセージが最大${n(removal.message_count)}件${photos}消えます。`;
  }
  const what = `${removal.label}を${removal.from}から${removal.to}にすると、`;
  if (removal.record_count === 0) return `${what}今夜の処理で消える記録はありません。`;
  return `${what}今夜の処理で、操作履歴とCSVの取り込みの記録が最大${n(removal.record_count)}件消えます。`;
}

function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}

/** 1つの設定の欄（名前・説明・「まだ効きません」・入力・既定値に戻す）。 */
function SettingField({
  setting,
  draft,
  errors,
  resetting,
  locked,
  onChange,
  onReset,
}: {
  setting: SettingItem;
  draft: SettingDraft | undefined;
  errors: Record<string, string>;
  resetting: boolean;
  /** 保存期間の設定で、「保存期間を変える」を持たない方（入力できない） */
  locked: boolean;
  onChange: (draft: SettingDraft) => void;
  onReset: () => void;
}): ReactNode {
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  if (!draft) return null;
  const inputId = `setting-${setting.key.replace(/[^a-z0-9]/gi, '-')}`;
  const noteId = `${inputId}-note`;
  const atDefault = sameSettingValue(setting, fromDraft(setting, draft), setting.default_value);
  return (
    <div className="admin-setting-field">
      <div className="admin-setting-head">
        {setting.editor === 'switch' ? (
          <span className="admin-setting-label">{setting.label}</span>
        ) : (
          <label className="admin-setting-label" htmlFor={inputId}>
            {setting.label}
          </label>
        )}
        <span className="admin-tags">
          {setting.in_effect ? null : <span className="admin-badge">まだ効きません</span>}
          {resetting ? (
            <span className="admin-tag admin-tag-neutral">保存すると既定値に戻ります</span>
          ) : null}
        </span>
      </div>
      <p className="admin-note" id={noteId}>
        {setting.description}
        {setting.effect_note ? (
          <>
            <br />
            {setting.effect_note}
          </>
        ) : null}
        {locked ? (
          <>
            <br />
            {/* 役職の呼び名はその会社のもの（issue #480） */}
            {wording.only(ROLE_REQUIREMENTS.orgRetention, '保存日数・保存年数を変えられるのは')}
          </>
        ) : null}
      </p>
      <SettingEditorInput
        setting={setting}
        draft={draft}
        errors={errors}
        inputId={inputId}
        noteId={noteId}
        disabled={locked}
        onChange={onChange}
      />
      <div className="admin-row-actions">
        {TABLE_EDITORS.has(setting.editor) ? null : (
          <small className="admin-setting-default">
            既定値：{settingValueText(setting, setting.default_value)}
          </small>
        )}
        {!locked && !atDefault ? (
          <Button variant="quiet" compact onClick={onReset}>
            既定値に戻す
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function SettingEditorInput({
  setting,
  draft,
  errors,
  inputId,
  noteId,
  disabled,
  onChange,
}: {
  setting: SettingItem;
  draft: SettingDraft;
  errors: Record<string, string>;
  inputId: string;
  noteId: string;
  disabled: boolean;
  onChange: (draft: SettingDraft) => void;
}): ReactNode {
  switch (draft.kind) {
    case 'text': {
      if (setting.editor === 'long_text') {
        return (
          <textarea
            id={inputId}
            rows={3}
            value={draft.text}
            disabled={disabled}
            aria-describedby={noteId}
            {...(setting.max_length !== null ? { maxLength: setting.max_length } : {})}
            onChange={(event) => onChange({ kind: 'text', text: event.target.value })}
          />
        );
      }
      const numeric = setting.editor === 'number' || setting.editor === 'megabytes';
      // 数の誤りは欄の下に日本語で出す。サーバ側の検査も保存時に行われる。
      return (
        <>
          <div className={numeric ? 'admin-setting-number' : undefined}>
            <input
              id={inputId}
              type={numeric ? 'number' : setting.editor === 'time' ? 'time' : 'text'}
              aria-invalid={errors.value ? true : undefined}
              value={draft.text}
              disabled={disabled}
              aria-describedby={errors.value ? `${noteId} ${inputId}-error` : noteId}
              {...(numeric
                ? { inputMode: 'numeric' as const, step: 1 }
                : setting.editor === 'url'
                  ? { inputMode: 'url' as const }
                  : {})}
              {...(!numeric && setting.max_length !== null
                ? { maxLength: setting.max_length }
                : {})}
              onChange={(event) => onChange({ kind: 'text', text: event.target.value })}
            />
            {numeric && setting.unit ? <span>{setting.unit}</span> : null}
            {setting.editor === 'number' ? <small>{rangeText(setting)}</small> : null}
          </div>
          {errors.value ? (
            <p className="admin-field-error" id={`${inputId}-error`} role="alert">
              {errors.value}
            </p>
          ) : null}
        </>
      );
    }
    case 'switch':
      return (
        <label className="ui-check" htmlFor={inputId}>
          <input
            id={inputId}
            type="checkbox"
            checked={draft.on}
            disabled={disabled}
            aria-describedby={noteId}
            onChange={(event) => onChange({ kind: 'switch', on: event.target.checked })}
          />
          <span>{switchWord(setting.label, true)}</span>
        </label>
      );
    case 'presets':
      return (
        <PresetTableInput
          inputId={inputId}
          table={draft.table}
          disabled={disabled}
          onChange={(table) => onChange({ kind: 'presets', table })}
        />
      );
    case 'retention':
      return (
        <DataTable label={setting.label}>
          <thead>
            <tr>
              <th scope="col">トークの種類</th>
              <th scope="col">保存日数</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(draft.rows).map(([type, row], index) => (
              <tr key={type}>
                <th scope="row">{channelTypeLabel(type)}</th>
                <td className="ui-cell-phrase">
                  <div className="admin-setting-number">
                    <input
                      // 最初の行を、欄の名前（label）から選べるようにする
                      {...(index === 0 ? { id: inputId } : {})}
                      type="number"
                      inputMode="numeric"
                      step={1}
                      aria-label={`${channelTypeLabel(type)}の保存日数`}
                      aria-invalid={errors[type] ? true : undefined}
                      aria-describedby={errors[type] ? `${inputId}-${type}-error` : noteId}
                      value={row.unlimited ? '' : row.days}
                      disabled={disabled || row.unlimited}
                      onChange={(event) =>
                        onChange({
                          kind: 'retention',
                          rows: { ...draft.rows, [type]: { ...row, days: event.target.value } },
                        })
                      }
                    />
                    <span>日</span>
                  </div>
                  {errors[type] ? (
                    <p className="admin-field-error" id={`${inputId}-${type}-error`} role="alert">
                      {errors[type]}
                    </p>
                  ) : null}
                  <label className="ui-check">
                    <input
                      type="checkbox"
                      checked={row.unlimited}
                      disabled={disabled}
                      onChange={(event) =>
                        onChange({
                          kind: 'retention',
                          rows: {
                            ...draft.rows,
                            [type]: { ...row, unlimited: event.target.checked },
                          },
                        })
                      }
                    />
                    <span>
                      無期限
                      <wbr />
                      （自動では消さない）
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      );
    case 'template':
      return (
        <SiteTemplateInput
          inputId={inputId}
          label={setting.label}
          rows={draft.rows}
          disabled={disabled}
          onChange={(rows) => onChange({ kind: 'template', rows })}
        />
      );
    case 'types': {
      const all = (setting.default_value ?? {}) as AllowedTypes;
      return (
        <fieldset className="admin-choice-group" id={inputId} aria-describedby={noteId}>
          <legend className="admin-visually-hidden">{setting.label}</legend>
          {Object.entries(all).map(([contentType, extensions]) => (
            <label key={contentType} className="ui-check">
              <input
                type="checkbox"
                checked={draft.checked.includes(contentType)}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    kind: 'types',
                    checked: event.target.checked
                      ? [...draft.checked, contentType]
                      : draft.checked.filter((type) => type !== contentType),
                  })
                }
              />
              <span>{attachmentTypeLabel(contentType, extensions)}</span>
            </label>
          ))}
        </fieldset>
      );
    }
  }
}

/** トークの種類ごとの決まりの表（メッセージを送れる方・既読の見せ方・通知に本文を載せる）。 */
function PresetTableInput({
  inputId,
  table,
  disabled,
  onChange,
}: {
  inputId: string;
  table: PresetTable;
  disabled: boolean;
  onChange: (table: PresetTable) => void;
}): ReactNode {
  const types = sortedTypes(Object.keys(table));
  function update(type: string, field: string, value: unknown): void {
    const current = table[type];
    if (!current) return;
    onChange({ ...table, [type]: { ...current, [field]: value } });
  }
  return (
    <DataTable label="トークの種類ごとの決まり">
      <thead>
        <tr>
          <th scope="col">トークの種類</th>
          <th scope="col">メッセージを送れる方</th>
          <th scope="col">既読の見せ方</th>
          <th scope="col">通知に本文を載せる</th>
        </tr>
      </thead>
      <tbody>
        {types.map((type, index) => {
          const preset = table[type]!;
          const fixed = !canChoosePostPolicy(type);
          return (
            <tr key={type}>
              <th scope="row">{channelTypeLabel(type)}</th>
              <td>
                <select
                  {...(index === 0 ? { id: inputId } : {})}
                  aria-label={`${channelTypeLabel(type)}のメッセージを送れる方`}
                  value={preset.post_policy}
                  disabled={disabled || fixed}
                  onChange={(event) => update(type, 'post_policy', event.target.value)}
                >
                  <option value="members">{postPolicyLabel('members')}</option>
                  <option value="admins">{postPolicyLabel('admins')}</option>
                </select>
                {fixed ? <small>このトークでは変えられません</small> : null}
              </td>
              <td>
                <select
                  aria-label={`${channelTypeLabel(type)}の既読の見せ方`}
                  value={preset.read_receipt_display}
                  disabled={disabled}
                  onChange={(event) => update(type, 'read_receipt_display', event.target.value)}
                >
                  <option value="count">{readReceiptLabel('count')}</option>
                  <option value="none">{readReceiptLabel('none')}</option>
                </select>
              </td>
              <td>
                <label className="ui-check">
                  <input
                    type="checkbox"
                    aria-label={`${channelTypeLabel(type)}の通知に本文を載せる`}
                    checked={preset.notify_with_body}
                    disabled={disabled}
                    onChange={(event) => update(type, 'notify_with_body', event.target.checked)}
                  />
                  <span>載せる</span>
                </label>
              </td>
            </tr>
          );
        })}
      </tbody>
    </DataTable>
  );
}

/**
 * 拠点のひな形（拠点を足したときに作るトーク・issue #517）の表。
 * 行ごとに種類と名前を入れ、「外す」で行を消し、「トークを足す」で行を足す（5つまで）。
 * 入れた内容は「保存する」を押すまで変わらない（ほかの欄と同じ）。
 */
function SiteTemplateInput({
  inputId,
  label,
  rows,
  disabled,
  onChange,
}: {
  inputId: string;
  label: string;
  rows: SiteTemplateRow[];
  disabled: boolean;
  onChange: (rows: SiteTemplateRow[]) => void;
}): ReactNode {
  function update(index: number, patch: Partial<SiteTemplateRow>): void {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }
  return (
    <div className="admin-site-template">
      {rows.length === 0 ? (
        <p className="admin-note" id={inputId}>
          いまは、拠点を足してもトークは作りません。
        </p>
      ) : (
        <DataTable label={label}>
          <thead>
            <tr>
              <th scope="col">トークの名前</th>
              <th scope="col">種類</th>
              <th scope="col">
                <span className="admin-visually-hidden">操作</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <input
                    {...(index === 0 ? { id: inputId } : {})}
                    type="text"
                    aria-label={`${index + 1}つめのトークの名前`}
                    value={row.name}
                    maxLength={SITE_TEMPLATE_NAME_MAX}
                    disabled={disabled}
                    onChange={(event) => update(index, { name: event.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label={`${index + 1}つめのトークの種類`}
                    value={row.type}
                    disabled={disabled}
                    onChange={(event) => update(index, { type: event.target.value })}
                  >
                    {SITE_TEMPLATE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {channelTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <Button
                    variant="secondary"
                    compact
                    disabled={disabled}
                    aria-label={`${index + 1}つめのトークを外す`}
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  >
                    外す
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      <div className="admin-row-actions">
        <Button
          variant="secondary"
          compact
          disabled={disabled || rows.length >= SITE_TEMPLATE_MAX}
          onClick={() => onChange([...rows, { type: 'official', name: '' }])}
        >
          トークを足す
        </Button>
        <small>
          {rows.length >= SITE_TEMPLATE_MAX
            ? `${SITE_TEMPLATE_MAX}つまでです。`
            : '例：「{拠点名}の連絡」は、「横浜支店」を足すと「横浜支店の連絡」になります。'}
        </small>
      </div>
    </div>
  );
}
